import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { EnvironmentService } from './environment.service';
import { roundPolygon } from './page-region/region-geometry';
import {
  AutomaticScale,
  NormalizedLine,
  PAGE_SCALE_COORDINATE_SYSTEM,
  PageScaleHistoryEntry,
  PageScaleState,
  RawPageScaleHistoryEntry,
  RawPageScaleState,
  ScaleWarning,
  TRANSCRIPT_DETECTOR_VERSION,
  toPageScaleHistoryEntry,
  toPageScaleState,
  toScaleWarnings,
} from '../components/project/pages/preview/measure-overlay/scale-calibration';

/** Identifies a plan page. Legacy single-file projects use file `1`. */
export interface PageScalePageRef {
  projectId: string;
  fileId: string;
  pageNumber: number;
}

/** What a caller supplies to save a manual calibration. */
export interface SavePageScaleRequest extends PageScalePageRef {
  /** The traced line in unrotated page-normalized coordinates. */
  reference: NormalizedLine;
  /** The real length exactly as typed, with its unit. */
  enteredValue: string;
  note?: string;
  /** The detected scale shown to the reviewer, kept beside the manual one. */
  automatic: AutomaticScale;
  /** The etag of the state the reviewer was looking at; empty if the page was never calibrated. */
  expectedEtag: string;
  /** The page content fingerprint the reviewer was looking at, when known. */
  expectedPageRevision?: string;
  pageSizeCorrectionFactor: number;
  /** True when the reviewer has seen and accepted the warnings for this calibration. */
  acknowledgeWarnings: boolean;
}

/** What happened to a request to change a page's calibration. */
export interface PageScaleWriteResult {
  /**
   * `applied`: saved. `conflict`: someone else changed it first, nothing saved. `needs-confirmation`:
   * the reviewer must accept `warnings` first. `page-changed`: the page content is not what was
   * traced, nothing saved.
   */
  outcome: 'applied' | 'conflict' | 'needs-confirmation' | 'page-changed';
  /** The page's scale state after the request. */
  state: PageScaleState;
  warnings: ScaleWarning[];
  /** Plain-language explanation for any outcome other than `applied`. */
  message: string;
}

/** A page-scale request that failed, with a message written for the reviewer. */
export class PageScaleError extends Error {
  constructor(
    message: string,
    readonly status: number
  ) {
    super(message);
    this.name = 'PageScaleError';
  }
}

interface RawWriteResponse {
  outcome?: string;
  state?: RawPageScaleState;
  warnings?: { code?: string; message?: string }[];
  message?: string;
}

/**
 * Reads, saves and resets the manual scale calibration of a plan page (Issue #2442).
 *
 * A calibration is shared page metadata, not a browser preference: it is stored by the backend so
 * that it survives a reload, a new browser session, and is the same for every user with access to
 * the project. The backend is also where the scale is derived and where conflicting edits are
 * detected, so this service only carries the reviewer's reference line and typed length there and
 * turns the answer into typed values.
 */
@Injectable({ providedIn: 'root' })
export class PageScaleCalibrationService {
  private http = inject(HttpClient);
  private env = inject(EnvironmentService);

  /** Reports which scale is in force for a page. */
  async get(page: PageScalePageRef): Promise<PageScaleState> {
    try {
      return toPageScaleState(await firstValueFrom(this.http.get<RawPageScaleState>(this.url(page))));
    } catch (error) {
      throw this.toError(error, 'The saved scale for this page could not be loaded.');
    }
  }

  /** Saves a manual calibration from a traced reference line. */
  async save(request: SavePageScaleRequest): Promise<PageScaleWriteResult> {
    const [start, end] = roundPolygon([request.reference.start, request.reference.end]);
    const hasAutomatic = request.automatic.scaleFactor !== null && request.automatic.scaleFactor > 0;
    const body = {
      start,
      end,
      coordinateSystem: PAGE_SCALE_COORDINATE_SYSTEM,
      enteredValue: request.enteredValue.trim(),
      note: request.note?.trim() ?? '',
      automaticCandidate: hasAutomatic
        ? {
            scaleText: request.automatic.scaleText ?? '',
            drawingScaleFactor: request.automatic.scaleFactor,
            detectorVersion: TRANSCRIPT_DETECTOR_VERSION,
          }
        : undefined,
      expectedEtag: request.expectedEtag,
      expectedPageRevision: request.expectedPageRevision ?? '',
      pageSizeCorrectionFactor: request.pageSizeCorrectionFactor,
      acknowledgeWarnings: request.acknowledgeWarnings,
    };
    try {
      return this.toWriteResult(await firstValueFrom(this.http.put<RawWriteResponse>(this.url(request), body)));
    } catch (error) {
      throw this.toError(error, 'The calibration could not be saved. Please try again.');
    }
  }

  /** Removes a page's manual calibration so that automatic detection applies again. */
  async reset(page: PageScalePageRef, expectedEtag: string, note = ''): Promise<PageScaleWriteResult> {
    try {
      return this.toWriteResult(
        await firstValueFrom(this.http.post<RawWriteResponse>(`${this.url(page)}:reset`, { expectedEtag, note }))
      );
    } catch (error) {
      throw this.toError(error, 'The calibration could not be reset. Please try again.');
    }
  }

  /** Lists the changes made to a page's calibration, newest first. */
  async history(page: PageScalePageRef): Promise<PageScaleHistoryEntry[]> {
    try {
      const raw = await firstValueFrom(
        this.http.get<{ entries?: RawPageScaleHistoryEntry[] }>(`${this.url(page)}/history`)
      );
      return (raw.entries ?? []).map(toPageScaleHistoryEntry);
    } catch (error) {
      throw this.toError(error, 'The history of this page’s scale could not be loaded.');
    }
  }

  private url(page: PageScalePageRef): string {
    return (
      `https://${this.env.getApiServer()}/v1/architectural-plans/${encodeURIComponent(page.projectId)}` +
      `/files/${encodeURIComponent(page.fileId)}/pages/${page.pageNumber}/scale-calibration`
    );
  }

  private toWriteResult(raw: RawWriteResponse): PageScaleWriteResult {
    let outcome: PageScaleWriteResult['outcome'];
    switch (raw.outcome) {
      case 'APPLIED':
        outcome = 'applied';
        break;
      case 'CONFLICT':
        outcome = 'conflict';
        break;
      case 'NEEDS_CONFIRMATION':
        outcome = 'needs-confirmation';
        break;
      case 'PAGE_CHANGED':
        outcome = 'page-changed';
        break;
      default:
        // An answer this client cannot interpret must not be reported as a successful save.
        throw new PageScaleError('The server gave an answer this version of the app does not understand.', 500);
    }
    // Every write answer carries the page's scale state. Without it there is nothing to show, and
    // an empty state must not be mistaken for "saved, and the page is on automatic detection".
    if (!raw.state) {
      throw new PageScaleError('The server gave an incomplete answer. Reload the page to see its scale.', 500);
    }
    return {
      outcome,
      state: toPageScaleState(raw.state),
      warnings: toScaleWarnings(raw.warnings),
      message: raw.message ?? '',
    };
  }

  private toError(error: unknown, fallback: string): PageScaleError {
    if (error instanceof PageScaleError) return error;
    if (error instanceof HttpErrorResponse) {
      if (error.status === 0) {
        return new PageScaleError(`${fallback} Check your connection.`, 0);
      }
      const message = (error.error as { message?: string } | undefined)?.message;
      return new PageScaleError(message || fallback, error.status);
    }
    return new PageScaleError(fallback, 500);
  }
}
